import { useCallback, useEffect, useState } from "react";
import "./index.css";
import { Dialog, DialogTrigger } from "components/Dialog";
import CreditMenu from "components/Dashboard/PeoplePanel/CreditMenu";
import TutoringUpfrontModal from "../Modals/Charges/TutoringUpfront";
import AutomaticPaymentsModal from "../Modals/Charges/PayPerSession";
import {
  PaymentPlanResponse,
  StudentAdminResponse,
  StudentSubjectAdminResponse,
  StudentSubjectsService,
  StudentsService,
} from "client/openapi";
import Notifications from "util/notifications";
import CreditDetails from "./creditDetails";
import { Button, ButtonSize, ButtonColor, ButtonFill } from "components/Button";

interface Credits {
  orgSubjectId: number;
  studentSubjectId: number;
  credits: number;
}

/**
 * A student's credits as the credits dialog shows them: each subject's,
 * the general credits and their total, kept current as the dialog edits
 * them (and the student's general credits with them).
 */
function useStudentCredits(
  student: StudentAdminResponse,
  setStudent: (student: StudentAdminResponse) => void
) {
  const [currentCredits, setCurrentCredits] = useState<Credits[]>([]);
  const [generalCredits, setGeneralCredits] = useState<number>(
    student.credits || 0
  );
  const [isLoadingCredits, setIsLoadingCredits] = useState<boolean>(true);

  useEffect(() => {
    setIsLoadingCredits(true);
    StudentSubjectsService.getStudentSubjectsByStudentId({
      studentId: student.id,
    })
      .then((studentSubjects) => {
        setCurrentCredits(
          (studentSubjects as StudentSubjectAdminResponse[]).map((subject) => ({
            orgSubjectId: subject.org_subject.id,
            studentSubjectId: subject.id,
            credits: subject.credits,
          }))
        );
      })
      .catch((error) => {
        Notifications.error(error?.message || "An unexpected error occurred.");
        console.error(`Error (#${error.status}): ${error.message}`);
      })
      .finally(() => setIsLoadingCredits(false));
  }, [student.id]);

  // Sync generalCredits with student prop when it changes
  useEffect(() => {
    setGeneralCredits(student.credits || 0);
  }, [student.credits]);

  const totalCredits =
    currentCredits.reduce((acc, credit) => acc + (credit.credits || 0), 0) +
    generalCredits;

  const handleCreditsUpdated = useCallback(
    (creditChanges: { orgSubjectId: number; newAmount: number }[]) => {
      // Update general credits if the change affects general credits
      const generalChange = creditChanges.find(
        (change) => change.orgSubjectId === -1
      );
      if (generalChange) {
        setGeneralCredits(generalChange.newAmount);
        // Update the student prop to keep it in sync
        setStudent({
          ...student,
          credits: generalChange.newAmount,
        });
      }

      // Update subject credits
      setCurrentCredits((prevCredits) =>
        prevCredits.map((credit) => {
          const change = creditChanges.find(
            (change) => change.orgSubjectId === credit.orgSubjectId
          );
          return change ? { ...credit, credits: change.newAmount } : credit;
        })
      );
    },
    [student, setStudent]
  );

  return {
    currentCredits,
    generalCredits,
    totalCredits,
    isLoadingCredits,
    handleCreditsUpdated,
  };
}

function Credit({
  student,
  orgId,
  setStudent,
}: {
  student: StudentAdminResponse;
  orgId: number;
  setStudent: (student: StudentAdminResponse) => void;
}) {
  const [paymentPlan, setPaymentPlan] = useState<PaymentPlanResponse>();
  const {
    currentCredits,
    generalCredits,
    totalCredits,
    isLoadingCredits,
    handleCreditsUpdated,
  } = useStudentCredits(student, setStudent);
  const [isLoadingPaymentPlan, setIsLoadingPaymentPlan] =
    useState<boolean>(true);
  // Credit Balance opens a menu: view the credits or charge for more.
  const [viewingCredits, setViewingCredits] = useState(false);
  const [charging, setCharging] = useState(false);

  useEffect(() => {
    setIsLoadingPaymentPlan(true);
    StudentsService.getPaymentPlan({
      studentId: student.id,
    })
      .then(setPaymentPlan)
      .catch((error) => {
        Notifications.error(error?.message || "An unexpected error occurred.");
        console.error(`Error (#${error.status}): ${error.message}`);
      })
      .finally(() => setIsLoadingPaymentPlan(false));
  }, [student.id]);

  return (
    <div className="credit-container mx-3 mt-3 space-y-4">
      <div>
        <CreditMenu
          onView={() => setViewingCredits(true)}
          onCharge={() => setCharging(true)}
        >
          <Button
            color={ButtonColor.DARK_BLUE}
            size={ButtonSize.DEFAULT}
            fill={ButtonFill.HOLLOW}
          >
            Credit Balance:{" "}
            {isLoadingCredits ? (
              <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></span>
            ) : (
              new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(totalCredits ? totalCredits / 100 : 0)
            )}
          </Button>
        </CreditMenu>
        <Dialog open={charging} onOpenChange={setCharging}>
          {charging && <TutoringUpfrontModal student={student} orgId={orgId} />}
        </Dialog>
        <Dialog open={viewingCredits} onOpenChange={setViewingCredits}>
          <CreditDetails
            student={student}
            orgId={orgId}
            currentStudentCredits={currentCredits}
            generalCredits={generalCredits}
            onCreditsUpdated={handleCreditsUpdated}
          />
        </Dialog>
      </div>
      <div>
        <Dialog>
          <DialogTrigger>
            <Button
              color={ButtonColor.DARK_BLUE}
              size={ButtonSize.DEFAULT}
              fill={ButtonFill.HOLLOW}
            >
              Auto Bill :{" "}
              {isLoadingPaymentPlan ? (
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></span>
              ) : paymentPlan?.frequency === "NONE" ? (
                "No"
              ) : (
                "Yes"
              )}
            </Button>
          </DialogTrigger>
          <AutomaticPaymentsModal
            student={student}
            orgId={orgId}
            paymentPlan={paymentPlan}
            setPaymentPlan={setPaymentPlan}
          />
        </Dialog>
      </div>
    </div>
  );
}
export default Credit;

/**
 * A student's credits dialog (the billing page's Credit Balance window),
 * over another page: the People panel's "Owes" chip.
 */
export function StudentCreditsDialog({
  studentId,
  orgId,
  onClose,
}: {
  studentId: number;
  orgId: number;
  onClose: () => void;
}) {
  const [student, setStudent] = useState<StudentAdminResponse>();
  useEffect(() => {
    StudentsService.getStudent({ studentId })
      .then((found) => setStudent(found as StudentAdminResponse))
      .catch(() => {
        Notifications.error("Could not open the credits.");
        onClose();
      });
  }, [studentId, onClose]);

  return student ? (
    <LoadedCreditsDialog
      student={student}
      setStudent={setStudent}
      orgId={orgId}
      onClose={onClose}
    />
  ) : null;
}

function LoadedCreditsDialog({
  student,
  setStudent,
  orgId,
  onClose,
}: {
  student: StudentAdminResponse;
  setStudent: (student: StudentAdminResponse) => void;
  orgId: number;
  onClose: () => void;
}) {
  const credits = useStudentCredits(student, setStudent);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      {!credits.isLoadingCredits && (
        <CreditDetails
          student={student}
          orgId={orgId}
          currentStudentCredits={credits.currentCredits}
          generalCredits={credits.generalCredits}
          onCreditsUpdated={credits.handleCreditsUpdated}
        />
      )}
    </Dialog>
  );
}
